// VerifyPage — inter-operator subscriber lookup (W26).
//
// Operator enters a PAN or phone, hits Lookup, sees aggregate counts only.
// No tenant names, no subscriber names — by design.

import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

interface LookupResult {
  active_at: number;
  defaulted_at: number;
  archived_at: number;
  matched_tenants: number;
  remaining_quota_today: number;
}

export function VerifyPage(): ReactElement {
  const qc = useQueryClient();
  const [pan, set_pan] = useState('');
  const [phone, set_phone] = useState('+91');
  const [result, set_result] = useState<LookupResult | null>(null);

  const consent_q = useQuery({
    queryKey: ['verification-consent'],
    queryFn: () => orpc.verification.get_consent({}),
  });

  const consent_m = useMutation({
    mutationFn: (consent: 'shared' | 'private') => orpc.verification.set_consent({ consent }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['verification-consent'] }),
  });

  const lookup_m = useMutation({
    mutationFn: (input: { pan?: string; phone?: string }) =>
      orpc.verification.lookup(input as { pan?: string; phone?: string }),
    onSuccess: (r) => set_result(r as LookupResult),
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    set_result(null);
    const input: { pan?: string; phone?: string } = {};
    if (pan.trim()) input.pan = pan.trim().toUpperCase();
    if (phone.trim() && phone.trim() !== '+91') input.phone = phone.trim();
    if (!input.pan && !input.phone) return;
    lookup_m.mutate(input);
  };

  const consent = consent_q.data?.consent ?? 'private';

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-medium">Inter-operator verification</h1>
        <p className="text-xs text-ink/60 mt-1">
          Look up subscribers across opted-in chit operators. Aggregate counts only — no names,
          no contact info.
        </p>
      </div>

      <section className="bg-surface border border-line rounded-lg p-5 mb-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Reciprocity</div>
            <div className="text-xs text-ink/60 mt-1">
              You can query the network only if you contribute. Toggling to <strong>shared</strong>{' '}
              publishes hashed identifiers (PAN, phone) of your subscribers — never raw values.
            </div>
          </div>
          <button
            type="button"
            onClick={() => consent_m.mutate(consent === 'shared' ? 'private' : 'shared')}
            disabled={consent_m.isPending || consent_q.isLoading}
            className={`text-xs px-3 py-1.5 rounded font-medium ${
              consent === 'shared'
                ? 'bg-good text-white hover:bg-good/90'
                : 'bg-bg-elev border border-line text-ink/70 hover:bg-surface'
            } disabled:opacity-50`}
          >
            {consent === 'shared' ? '✓ Shared (opted in)' : 'Opt in'}
          </button>
        </div>
      </section>

      <form
        onSubmit={submit}
        className="bg-surface border border-line rounded-lg p-5 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3"
      >
        <label>
          <span className="block text-xs font-medium text-ink/70 mb-1">PAN (optional)</span>
          <input
            type="text"
            value={pan}
            onChange={(e) => set_pan(e.target.value.toUpperCase().slice(0, 10))}
            placeholder="ABCDE1234F"
            className="w-full px-3 py-2 border border-line rounded text-sm font-mono uppercase"
            maxLength={10}
          />
        </label>
        <label>
          <span className="block text-xs font-medium text-ink/70 mb-1">Phone (optional)</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => set_phone(e.target.value)}
            placeholder="+919876543210"
            className="w-full px-3 py-2 border border-line rounded text-sm font-mono"
          />
        </label>
        <div className="md:col-span-2 flex items-center gap-3">
          <button
            type="submit"
            disabled={lookup_m.isPending || consent !== 'shared'}
            className="bg-primary hover:bg-primary-2 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
          >
            {lookup_m.isPending ? '…' : 'Look up'}
          </button>
          {consent !== 'shared' && (
            <span className="text-xs text-warn">
              Reciprocity required — opt in above to query.
            </span>
          )}
        </div>
      </form>

      {lookup_m.error && (
        <div className="bg-bad-soft text-bad px-4 py-3 rounded text-sm mb-4">
          {friendly_error(lookup_m.error)}
        </div>
      )}

      {result && (
        <div className="bg-surface border border-line rounded-lg p-5">
          <div className="text-sm font-medium mb-3">Result</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Tile label="Active at" value={result.active_at} tone="good" />
            <Tile label="Defaulted at" value={result.defaulted_at} tone="bad" />
            <Tile label="Archived at" value={result.archived_at} />
            <Tile label="Matched tenants" value={result.matched_tenants} tone="info" />
          </div>
          <div className="text-[11px] text-ink/50">
            {result.remaining_quota_today} lookups remaining today.
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'good' | 'bad' | 'info';
}): ReactElement {
  const cls =
    tone === 'good'
      ? 'text-good'
      : tone === 'bad'
        ? 'text-bad'
        : tone === 'info'
          ? 'text-info'
          : 'text-ink';
  return (
    <div className="bg-bg-elev rounded p-3">
      <div className="text-[10px] uppercase tracking-wider text-ink/50">{label}</div>
      <div className={`text-2xl font-mono mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
